import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/lib/constants";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-white border-b border-black/10" : "bg-white/85 backdrop-blur border-b border-black/5"}`}
    >
      <div className="mx-auto max-w-[1480px] px-6 lg:px-8 h-20 flex items-center justify-between gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group shrink-0" aria-label="Velor Advisory home">
          <span className="h-20 overflow-hidden flex items-center">
            <img
              src={BRAND.logoBlack}
              alt="Velor Advisory logo"
              className="h-[150px] md:h-[170px] w-auto object-contain"
              draggable="false"
            />
          </span>
          <span className="hidden sm:inline text-[10px] tracking-overline uppercase text-black/55 border-l border-black/15 pl-3 whitespace-nowrap">Advisory · UAE</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8" data-testid="primary-nav">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-[12px] xl:text-[13px] tracking-overline uppercase whitespace-nowrap transition-colors ${isActive ? "text-black" : "text-black/55 hover:text-black"}`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={BRAND.calendly}
            target="_blank"
            rel="noreferrer"
            data-testid="header-book-consultation-btn"
            className="inline-flex items-center px-5 py-3 text-[12px] tracking-overline uppercase whitespace-nowrap bg-black text-white hover:bg-black/85 transition-colors"
          >
            Book Consultation
          </a>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className="lg:hidden p-2"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 ease-in-out border-t border-black/5 ${open ? "max-h-[640px]" : "max-h-0"}`}
        data-testid="mobile-nav"
      >
        <div className="px-6 py-6 flex flex-col gap-1 bg-white">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `py-3 text-[13px] tracking-overline uppercase border-b border-black/5 ${isActive ? "text-black" : "text-black/60"}`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={BRAND.calendly}
            target="_blank"
            rel="noreferrer"
            data-testid="mobile-book-consultation-btn"
            className="mt-5 inline-flex items-center justify-center px-5 py-4 text-[12px] tracking-overline uppercase bg-black text-white"
          >
            Book Free Consultation
          </a>
        </div>
      </div>
    </header>
  );
}
